
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const useOgImage = () => {
  const [ogImageExists, setOgImageExists] = useState<boolean | null>(null);
  const [isCheckingImage, setIsCheckingImage] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [ogImageFileName, setOgImageFileName] = useState<string>("og-image.png");
  const [ogImageCacheBuster, setOgImageCacheBuster] = useState<string | null>(null);
  const { toast } = useToast();
  
  const bucketBaseUrl = "https://skdignrsplwwxoiulvdb.supabase.co/storage/v1/object/public/images/";
  const fullOgImageUrl = `${bucketBaseUrl}${ogImageFileName}`;
  const displayImageUrl = ogImageCacheBuster 
    ? `${fullOgImageUrl}?t=${ogImageCacheBuster}` 
    : fullOgImageUrl;

  const checkImageExists = async (url: string): Promise<boolean> => {
    setIsCheckingImage(true);
    try {
      console.log("Checking image existence at:", url);
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${url}?t=${cacheBuster}`, { 
        method: 'HEAD',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log("Image check response:", response.status, response.ok);
      
      setIsCheckingImage(false);
      setLastChecked(new Date());
      const exists = response.ok;
      setOgImageExists(exists);
      return exists;
    } catch (error) {
      console.error("Error checking image:", error);
      setIsCheckingImage(false);
      setLastChecked(new Date());
      setOgImageExists(false);
      return false;
    }
  };

  const manualCheckImage = async () => {
    const urlToCheck = fullOgImageUrl;
    setOgImageCacheBuster(new Date().getTime().toString());
      
    const exists = await checkImageExists(urlToCheck);
    
    if (exists) {
      toast({
        title: "Bild wurde gefunden",
        description: "Das Social Media Vorschaubild ist korrekt verfügbar.",
      });
    } else {
      toast({
        variant: "destructive",
        title: "Bild nicht gefunden!",
        description: "Das Social Media Vorschaubild konnte nicht gefunden werden. Bitte laden Sie es erneut hoch.",
      });
    }
  };

  // Set up OG image event listener
  useEffect(() => {
    const handleOgImageUpdated = (e: CustomEvent) => {
      if (e.detail) {
        console.log("OG image updated event received with details:", e.detail);
        
        if (e.detail.cacheBuster) {
          setOgImageCacheBuster(e.detail.cacheBuster);
        } else {
          setOgImageCacheBuster(new Date().getTime().toString());
        }
        
        if (e.detail.fileName) {
          setOgImageFileName(e.detail.fileName);
        }
        
        setTimeout(() => {
          checkImageExists(`${fullOgImageUrl}?t=${new Date().getTime()}`);
          
          setTimeout(() => {
            checkImageExists(`${fullOgImageUrl}?t=${new Date().getTime()}`);
            
            setTimeout(() => {
              checkImageExists(`${fullOgImageUrl}?t=${new Date().getTime()}`);
            }, 2000);
          }, 1000);
        }, 500);
      }
    };

    window.addEventListener('og-image-updated', handleOgImageUpdated as EventListener);
    
    return () => {
      window.removeEventListener('og-image-updated', handleOgImageUpdated as EventListener);
    };
  }, [fullOgImageUrl]);

  return {
    ogImageExists,
    isCheckingImage,
    lastChecked,
    displayImageUrl,
    fullOgImageUrl,
    manualCheckImage,
    checkImageExists
  };
};
